/**
 * Single source of truth for which users are selectable in operator-facing
 * user lists (ticket assignees, pin assignment/reassignment dropdowns).
 * Imported by both the API worker (workers/src) and the frontend (frontend/src).
 *
 * Deactivating a user in Prism sets is_active=false but keeps their roles,
 * so every consumer of the Python /api/v1/users list MUST filter on
 * is_active — a role check alone re-includes deactivated users.
 * The admin user-management screen is the one deliberate exception: it
 * shows deactivated users so they can be re-enabled, and must not use
 * these helpers.
 */

export function isActiveUser(user) {
    return user.is_active !== false;
}

/** Normalize a users API response (bare array or {users}/{data} wrapper) to active users only. */
export function activeUsersFrom(response) {
    const users = Array.isArray(response) ? response : (response?.users || response?.data || []);
    return users.filter(isActiveUser);
}

/** Users who can be assigned tickets: active admins + active Customer Service. */
export function filterTicketAssignees(allUsers) {
    return allUsers
        .filter(isActiveUser)
        .filter(u => u.is_admin || (u.roles && u.roles.some(r => r.name === 'Customer Service')))
        .map(u => ({
            user_id: u.user_id || u.id,
            name: u.name,
            is_admin: u.is_admin || false
        }));
}

/**
 * Users a quote can be assigned to for second review (spec
 * docs/archive/202609/specs_20260916_quote_review.md): active holders of the Deals Reviewer
 * role only. Admins are deliberately excluded from the picker (feedback
 * 2026-09-17) — a quote is assigned to a reviewer, not an admin. Same shape as
 * filterTicketAssignees.
 */
export function filterQuoteReviewAssignees(allUsers) {
    return activeUsersFrom(allUsers)
        // Only users with the explicit Deals Reviewer role — admins are excluded
        // from the assign/reassign picker by request.
        .filter(u => u.roles && u.roles.some(r => r.name === 'Deals Reviewer'))
        .map(u => ({
            user_id: u.user_id || u.id,
            name: u.name,
            is_admin: u.is_admin || false
        }));
}
